import React, { useCallback, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CampaignIcon from "@mui/icons-material/Campaign";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DOMPurify from "dompurify";
import { NewsCategory, NewsItem } from "@/shared/interfaces/NewsItem";
import { formatNewsTimestamp } from "@/shared/utils/newsFilters";

interface NewsModalProps {
  open: boolean;
  onClose: () => void;
  newsItems: NewsItem[];
}

const categoryIcons: Record<NewsCategory, typeof InfoIcon> = {
  MAINTENANCE: BuildIcon,
  FEATURE: NewReleasesIcon,
  ALERT: WarningIcon,
  INFO: InfoIcon,
};

const categoryColors: Record<NewsCategory, string> = {
  MAINTENANCE: "#8b5cf6",
  FEATURE: "#10b981",
  ALERT: "#f59e0b",
  INFO: "#6366f1",
};

const categoryLabels: Record<NewsCategory, string> = {
  MAINTENANCE: "Maintenance",
  FEATURE: "New Feature",
  ALERT: "Alert",
  INFO: "Update",
};

/**
 * Renders self-contained HTML content (including a full <html>/<head>/<style>
 * document) inside a sandboxed iframe so its CSS is isolated from the app and
 * its <style> block survives. The iframe is sandboxed without `allow-scripts`,
 * so any embedded JS is neutralized; `allow-same-origin` lets us measure the
 * content height for auto-sizing.
 */
const NewsContent: React.FC<{ content: string }> = ({ content }) => {
  const [height, setHeight] = useState(0);

  // Strip scripts/handlers but keep the whole document (head + style block).
  // Append an overflow override so the iframe never shows its own scrollbar —
  // the iframe is auto-sized to the content height instead.
  const srcDoc = useMemo(() => {
    const clean = DOMPurify.sanitize(content, {
      WHOLE_DOCUMENT: true,
      FORBID_TAGS: ["script"],
      FORBID_ATTR: ["onerror", "onload", "onclick"],
    });
    const override = "<style>html,body{overflow:hidden!important;}</style>";
    return clean.includes("</head>")
      ? clean.replace("</head>", `${override}</head>`)
      : `${override}${clean}`;
  }, [content]);

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const doc = e.currentTarget.contentDocument;
      if (doc?.documentElement) {
        setHeight(
          Math.max(
            doc.documentElement.scrollHeight,
            doc.body?.scrollHeight ?? 0
          )
        );
      }
    },
    []
  );

  return (
    <Box
      component="iframe"
      title="news-content"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      sandbox="allow-same-origin"
      sx={{
        width: "100%",
        height: height ? `${height}px` : 200,
        border: "none",
        display: "block",
      }}
    />
  );
};

export const NewsModal: React.FC<NewsModalProps> = ({
  open,
  onClose,
  newsItems,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="news-modal-title"
      aria-describedby="news-modal-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          zIndex: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: { xs: 2.5, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          >
            <CampaignIcon sx={{ fontSize: 26, color: "white" }} />
          </Box>

          <Typography
            id="news-modal-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}
          >
            What's New
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {newsItems.length === 0
              ? "No new updates"
              : `${newsItems.length} update${
                  newsItems.length === 1 ? "" : "s"
                } for you`}
          </Typography>
        </Box>

        {newsItems.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              px: 2,
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 48,
                color: "success.main",
                mb: 1.5,
              }}
            />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "text.primary", mb: 0.5 }}
            >
              You're all caught up!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check back later for new updates and announcements.
            </Typography>
          </Box>
        ) : (
          <Stack
            spacing={2}
            sx={{
              maxHeight: 500,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: 5,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.primary, 0.15),
                borderRadius: 3,
              },
            }}
          >
            {newsItems.map((item) => {
              const categoryColor =
                categoryColors[item.category] || theme.palette.primary.main;
              const IconComponent = categoryIcons[item.category] || InfoIcon;
              return (
                <Paper
                  key={item.newsId}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: alpha(theme.palette.grey[500], 0.04),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    borderRadius: 2.5,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: alpha(categoryColor, 0.06),
                      borderColor: alpha(categoryColor, 0.3),
                    },
                  }}
                >
                  {/* Category badge + timestamp row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      icon={<IconComponent sx={{ fontSize: 14 }} />}
                      label={categoryLabels[item.category] || "Update"}
                      size="small"
                      sx={{
                        height: 26,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        backgroundColor: alpha(categoryColor, 0.12),
                        color: categoryColor,
                        borderRadius: 2,
                        "& .MuiChip-icon": {
                          color: categoryColor,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                      }}
                    >
                      {formatNewsTimestamp(item.createdAt)}
                    </Typography>
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      wordBreak: "break-word",
                      color: "text.primary",
                      lineHeight: 1.4,
                      mb: 1,
                    }}
                  >
                    {item.title}
                  </Typography>

                  {/* Content */}
                  <NewsContent content={item.content} />
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          pt: 2,
          justifyContent: "center",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            py: 1,
            px: 5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              background: `linear-gradient(135deg, ${
                theme.palette.primary.dark
              } 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
              transform: "translateY(-1px)",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};
